"use client";

import { useState, useRef, useEffect } from "react";
import { MessageCircle, ThumbsDown } from "lucide-react";

const ScoopsCard = ({ post, images = [], className = "" }) => {
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isContentLong, setIsContentLong] = useState(false);
  const contentRef = useRef(null);

  // Check content length after component mounts
  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      const containerHeight = 72; // 3 lines of text approx
      setIsContentLong(contentHeight > containerHeight);
    }
  }, [post.contentinfo, isContentExpanded]);

  // Function to calculate time ago from post creation date
  const getTimeAgo = (createdDate) => {
    if (!createdDate) return "";

    const created = new Date(createdDate);
    const now = new Date();
    const diffInSeconds = Math.floor((now - created) / 1000);

    const formatTime = (date) => {
      return date.toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    };

    // Just now (less than a minute)
    if (diffInSeconds < 60) {
      return "Just now";
    }

    // Minutes (less than an hour)
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    // Hours (less than a day)
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }

    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (
      created.getDate() === yesterday.getDate() &&
      created.getMonth() === yesterday.getMonth() &&
      created.getFullYear() === yesterday.getFullYear()
    ) {
      return `Yesterday at ${formatTime(created)}`;
    }

    // This week (less than 7 days)
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const dayOfWeek = dayNames[created.getDay()];
      return `${dayOfWeek} at ${formatTime(created)}`;
    }

    // Format month and day
    const month = created.toLocaleString("en-US", { month: "long" });
    const day = created.getDate();
    if (created.getFullYear() !== now.getFullYear()) {
      return `${month} ${day}, ${created.getFullYear()}`;
    }
    return `${month} ${day} at ${formatTime(created)}`;
  };

  const timeAgo = getTimeAgo(post.createdate);

  // Toggle content expansion
  const toggleContentExpansion = () => {
    setIsContentExpanded(!isContentExpanded);
  };

  // Function to process reactions and group them by emoji type (display only)
  const processReactions = (likes) => {
    if (!likes || !Array.isArray(likes)) return {};

    const groups = {};
    likes.forEach((like) => {
      if (like.emoji) {
        if (!groups[like.emoji]) {
          groups[like.emoji] = {
            count: 1,
            users: [like.username || ""],
          };
        } else {
          groups[like.emoji].count += 1;
          if (like.username) {
            groups[like.emoji].users.push(like.username);
          }
        }
      }
    });

    return groups;
  };

  // Process reaction groups from post data
  const reactionGroups = processReactions(post.likes || []);

  // Reaction Bubbles component (display only)
  const ReactionBubbles = ({ reactionGroups }) => {
    if (!reactionGroups || Object.keys(reactionGroups).length === 0)
      return null;

    return (
      <div className="flex -space-x-1 overflow-hidden">
        {Object.entries(reactionGroups).map(([emoji, data], index) => (
          <div
            key={index}
            className="inline-block h-6 w-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-sm shadow-sm"
            title={`${data.users.slice(0, 3).join(", ")}${
              data.users.length > 3
                ? ` and ${data.users.length - 3} others`
                : ""
            }`}
          >
            {emoji}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center">
          <div className="h-8 w-8 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
            {post && post.avatar_url ? (
              <img
                src={post.avatar_url || "/placeholder.svg"}
                alt={post.username}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={"/placeholder.svg"}
                alt={post.username}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="ml-2 min-w-0">
            <div className="font-medium text-sm text-gray-900 truncate">
              {post.username}
            </div>
            <div className="text-xs text-gray-500">{timeAgo}</div>
          </div>
        </div>
        {/* Three dots menu */}
        <div className="flex-shrink-0">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="5" r="2" fill="#6b7280" />
            <circle cx="12" cy="12" r="2" fill="#6b7280" />
            <circle cx="12" cy="19" r="2" fill="#6b7280" />
          </svg>
        </div>
      </div>

      {/* Category and Hashtags */}
      <div className="px-3 pb-2 flex flex-wrap items-center gap-2">
        {post.scoopname && (
          <div className="bg-black text-white font-medium text-xs rounded-full px-3 py-1">
            {post.scoopname}
          </div>
        )}
        {post.hoops &&
          post.hoops.length > 0 &&
          post.hoops.map((tag, index) => (
            <span
              key={index}
              className="bg-fuchsia-100 text-fuchsia-800 text-xs px-2 py-1 rounded-full font-medium"
            >
              {tag.hooptag}
            </span>
          ))}
      </div>

      {/* Single Image Display */}
      {images && images.length > 0 && (
        <div className="relative">
          <div className="w-full relative bg-pink-50 overflow-hidden">
            <div className="relative w-full">
              <img
                src={images[0] || "/placeholder.svg"}
                alt="Post image"
                className="w-full h-auto max-h-[600px] object-contain"
                style={{
                  minHeight: "200px",
                  maxWidth: "100%",
                }}
                onLoad={(e) => {
                  const img = e.target;
                  const aspectRatio = img.naturalWidth / img.naturalHeight;

                  if (aspectRatio > 3) {
                    img.style.maxHeight = "300px";
                  } else if (aspectRatio < 0.5) {
                    img.style.maxHeight = "500px";
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-3">
        <p
          ref={contentRef}
          className={`text-sm text-gray-800 ${
            isContentExpanded ? "" : "line-clamp-3"
          }`}
        >
          {post.contentinfo}
        </p>
      </div>

      {/* Actions */}
      <div className="px-3 py-2 flex items-center justify-between border-t border-gray-100">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-500">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V13C2 12.4696 2.21071 11.9609 2.58579 11.5858C2.96086 11.2107 3.46957 11 4 11H7M14 9V5C14 4.20435 13.6839 3.44129 13.1213 2.87868C12.5587 2.31607 11.7956 2 11 2L7 11V22H18.28C18.7623 22.0055 19.2304 21.8364 19.5979 21.524C19.9654 21.2116 20.2077 20.7769 20.28 20.3L21.66 11.3C21.7035 11.0134 21.6842 10.7207 21.6033 10.4423C21.5225 10.1638 21.3821 9.90629 21.1919 9.68751C21.0016 9.46873 20.7661 9.29393 20.5016 9.17522C20.2371 9.0565 19.9499 8.99672 19.66 9H14Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex items-center ml-1">
              <ReactionBubbles reactionGroups={reactionGroups} />
              <span className="ml-1 text-sm">{post.totalLikes || 0}</span>
            </div>
          </div>
          <div className="flex items-center text-gray-500">
            <MessageCircle className="h-5 w-5" />
            <span className="ml-1 text-sm">{post.commentCount || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoopsCard;
